import React, { useEffect, useRef, useState } from 'react';
import {
	Autocomplete,
	Badge,
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { FilterList as FilterListIcon, Clear as ClearIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import {
	IProviderPayableLineListFilters,
	IProviderAutocomplete,
	ProviderPayableLinePaymentStatusEnum,
	ProviderPayableLinePaymentTypeEnum,
	ProviderPayableLineTypeEnum,
} from 'features/salesOrder/types';
import { getProviderAutocomplete } from 'features/salesOrder/services';
import { PROVIDER_PAYABLE_LINE_STATUS_LABEL, PROVIDER_PAYABLE_LINE_TYPE_LABEL } from '../../../constants';

const PAYMENT_TYPE_LABELS: Record<ProviderPayableLinePaymentTypeEnum, string> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'Tarjeta Corporativa',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'C2A',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'Transferencia Bancaria',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'Cheque',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'Efectivo',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'Otro',
};

const ALL_PAYMENT_TYPES = Object.values(ProviderPayableLinePaymentTypeEnum);
const ALL_STATUSES = Object.values(ProviderPayableLinePaymentStatusEnum);
const ALL_PAYABLE_TYPES = Object.values(ProviderPayableLineTypeEnum);
const ALL_MARKETS = ['UY', 'CO', 'PY', 'BO'];

interface LocalFilters {
	providers: IProviderAutocomplete[];
	ticket: string;
	saleOrderId: string;
	sellerMarket: string | null;
	payableTypes: ProviderPayableLineTypeEnum[];
	paymentTypes: ProviderPayableLinePaymentTypeEnum[];
	paymentStatuses: ProviderPayableLinePaymentStatusEnum[];
	dateFrom: Date | null;
	dateTo: Date | null;
	scheduleDateFrom: Date | null;
	scheduleDateTo: Date | null;
}

const defaultFilters: LocalFilters = {
	providers: [],
	ticket: '',
	saleOrderId: '',
	sellerMarket: null,
	payableTypes: [],
	paymentTypes: [],
	paymentStatuses: [],
	dateFrom: null,
	dateTo: null,
	scheduleDateFrom: null,
	scheduleDateTo: null,
};

/** Formatea Date a YYYY-MM-DD sin conversión UTC para evitar el problema del día -1 */
const toDateString = (d: Date | null): string | undefined => {
	if (!d) return undefined;
	return format(d, 'yyyy-MM-dd');
};

interface Props {
	onFiltersChange: (filters: IProviderPayableLineListFilters) => void;
	onClearFilters: () => void;
	hideProviderFilter?: boolean;
	embedded?: boolean;
}

export const ProviderPayablesFilters = ({
	onFiltersChange,
	onClearFilters,
	hideProviderFilter = false,
	embedded = false,
}: Props): JSX.Element => {
	const [filters, setFilters] = useState<LocalFilters>(defaultFilters);
	const [providerOptions, setProviderOptions] = useState<IProviderAutocomplete[]>([]);
	const [providerLoading, setProviderLoading] = useState(false);
	const [providerInput, setProviderInput] = useState('');

	const prevFiltersRef = useRef<string>('');
	const onFiltersChangeRef = useRef(onFiltersChange);

	useEffect(() => {
		onFiltersChangeRef.current = onFiltersChange;
	}, [onFiltersChange]);

	// Debounce autocomplete de proveedores (min 4 chars, igual que el componente existente)
	useEffect(() => {
		if (providerInput.length < 4) {
			setProviderOptions([]);
			return;
		}
		const timer = setTimeout(async () => {
			try {
				setProviderLoading(true);
				const res = await getProviderAutocomplete(providerInput);
				setProviderOptions(res.data);
			} catch {
				setProviderOptions([]);
			} finally {
				setProviderLoading(false);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [providerInput]);

	// Notificar al padre cuando cambian los filtros
	useEffect(() => {
		const toParent: IProviderPayableLineListFilters = {
			providerIds: filters.providers.length > 0 ? filters.providers.map((p) => String(p.id)) : undefined,
			ticket: filters.ticket || undefined,
			saleOrderId: filters.saleOrderId || undefined,
			sellerMarket: filters.sellerMarket || undefined,
			payableTypes: filters.payableTypes.length > 0 ? filters.payableTypes : undefined,
			paymentTypes: filters.paymentTypes.length > 0 ? filters.paymentTypes : undefined,
			paymentStatuses: filters.paymentStatuses.length > 0 ? filters.paymentStatuses : undefined,
			dateFrom: toDateString(filters.dateFrom),
			dateTo: toDateString(filters.dateTo),
			scheduleDateFrom: toDateString(filters.scheduleDateFrom),
			scheduleDateTo: toDateString(filters.scheduleDateTo),
		};
		const asString = JSON.stringify(toParent);
		if (prevFiltersRef.current !== asString) {
			prevFiltersRef.current = asString;
			onFiltersChangeRef.current(toParent);
		}
	}, [filters]);

	const handleChange = <K extends keyof LocalFilters>(field: K, value: LocalFilters[K]) => {
		setFilters((prev) => ({ ...prev, [field]: value }));
	};

	const handleClear = () => {
		setFilters(defaultFilters);
		setProviderInput('');
		onClearFilters();
	};

	const activeCount = [
		!hideProviderFilter && filters.providers.length > 0,
		!!filters.ticket,
		!!filters.saleOrderId,
		!!filters.sellerMarket,
		filters.payableTypes.length > 0,
		filters.paymentTypes.length > 0,
		filters.paymentStatuses.length > 0,
		!!filters.dateFrom,
		!!filters.dateTo,
		!!filters.scheduleDateFrom,
		!!filters.scheduleDateTo,
	].filter(Boolean).length;

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Paper elevation={2} sx={{ p: 2, mb: 2, mt: embedded ? 0 : 3 }}>
				{/* Header */}
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
					<Box display="flex" alignItems="center" gap={1}>
						<Badge badgeContent={activeCount} color="primary">
							<FilterListIcon color="action" />
						</Badge>
						<Typography variant="subtitle1" fontWeight={500}>
							Filtros:
						</Typography>
					</Box>

					{activeCount > 0 && (
						<Box display="flex" flexWrap="wrap" gap={0.5} flex={1}>
							{!hideProviderFilter &&
								filters.providers.map((p) => (
									<Chip
										key={p.id}
										label={p.fantasyName}
										size="small"
										color="primary"
										variant="outlined"
										onDelete={() =>
											handleChange(
												'providers',
												filters.providers.filter((x) => x.id !== p.id),
											)
										}
									/>
								))}
							{filters.ticket && (
								<Chip
									label={`Ticket: ${filters.ticket}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('ticket', '')}
								/>
							)}
							{filters.saleOrderId && (
								<Chip
									label={`Orden: ${filters.saleOrderId}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('saleOrderId', '')}
								/>
							)}
							{filters.sellerMarket && (
								<Chip
									label={`Mercado: ${filters.sellerMarket}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('sellerMarket', null)}
								/>
							)}
							{filters.payableTypes.map((t) => (
								<Chip
									key={t}
									label={`Línea: ${PROVIDER_PAYABLE_LINE_TYPE_LABEL[t]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										handleChange(
											'payableTypes',
											filters.payableTypes.filter((x) => x !== t),
										)
									}
								/>
							))}
							{filters.paymentTypes.map((t) => (
								<Chip
									key={t}
									label={`Tipo pago: ${PAYMENT_TYPE_LABELS[t]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										handleChange(
											'paymentTypes',
											filters.paymentTypes.filter((x) => x !== t),
										)
									}
								/>
							))}
							{filters.paymentStatuses.map((s) => (
								<Chip
									key={s}
									label={`Estado: ${PROVIDER_PAYABLE_LINE_STATUS_LABEL[s]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() =>
										handleChange(
											'paymentStatuses',
											filters.paymentStatuses.filter((x) => x !== s),
										)
									}
								/>
							))}
							{filters.dateFrom && (
								<Chip
									label={`Pago desde: ${format(filters.dateFrom, 'dd/MM/yyyy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('dateFrom', null)}
								/>
							)}
							{filters.dateTo && (
								<Chip
									label={`Pago hasta: ${format(filters.dateTo, 'dd/MM/yyyy')}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleChange('dateTo', null)}
								/>
							)}
							{filters.scheduleDateFrom && (
								<Chip
									label={`Sched. desde: ${format(filters.scheduleDateFrom, 'dd/MM/yyyy')}`}
									size="small"
									color="secondary"
									variant="outlined"
									onDelete={() => handleChange('scheduleDateFrom', null)}
								/>
							)}
							{filters.scheduleDateTo && (
								<Chip
									label={`Sched. hasta: ${format(filters.scheduleDateTo, 'dd/MM/yyyy')}`}
									size="small"
									color="secondary"
									variant="outlined"
									onDelete={() => handleChange('scheduleDateTo', null)}
								/>
							)}
						</Box>
					)}

					<Tooltip title="Limpiar todos los filtros">
						<Button size="small" startIcon={<ClearIcon />} onClick={handleClear} variant="outlined" color="error">
							Limpiar
						</Button>
					</Tooltip>
				</Box>

				<Grid container spacing={1.5}>
					{/* Proveedor */}
					{!hideProviderFilter && (
						<Grid item xs={12} sm={12} md={3}>
							<Autocomplete
								multiple
								options={providerOptions}
								value={filters.providers}
								inputValue={providerInput}
								loading={providerLoading}
								getOptionLabel={(o) => `${o.fantasyName} (${o.businessName})`}
								isOptionEqualToValue={(o, v) => o.id === v.id}
								onInputChange={(_, val) => setProviderInput(val)}
								onChange={(_, val) => handleChange('providers', val)}
								noOptionsText={providerInput.length < 4 ? 'Escribí al menos 4 caracteres' : 'Sin resultados'}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip {...getTagProps({ index })} key={option.id} label={option.fantasyName} size="small" />
									))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Proveedor"
										size="small"
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{providerLoading && <CircularProgress size={16} />}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>
						</Grid>
					)}

					{/* Ticket / Localizador */}
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							fullWidth
							size="small"
							label="Ticket / Localizador"
							value={filters.ticket}
							onChange={(e) => handleChange('ticket', e.target.value)}
						/>
					</Grid>

					{/* Orden de venta */}
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							fullWidth
							size="small"
							label="Orden de venta"
							value={filters.saleOrderId}
							onChange={(e) => handleChange('saleOrderId', e.target.value)}
						/>
					</Grid>

					{/* Mercado */}
					<Grid item xs={12} sm={6} md={1}>
						<Autocomplete
							options={ALL_MARKETS}
							value={filters.sellerMarket}
							getOptionLabel={(o) => o}
							onChange={(_, val) => handleChange('sellerMarket', val)}
							renderInput={(params) => <TextField {...params} label="Mercado" size="small" />}
						/>
					</Grid>

					{/* Tipo de línea — multiselect */}
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							options={ALL_PAYABLE_TYPES}
							value={filters.payableTypes}
							getOptionLabel={(o) => PROVIDER_PAYABLE_LINE_TYPE_LABEL[o]}
							onChange={(_, val) => handleChange('payableTypes', val)}
							disableCloseOnSelect
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										{...getTagProps({ index })}
										key={option}
										label={PROVIDER_PAYABLE_LINE_TYPE_LABEL[option]}
										size="small"
									/>
								))
							}
							renderInput={(params) => <TextField {...params} label="Tipo de línea" size="small" />}
						/>
					</Grid>

					{/* Tipo de pago — multiselect */}
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							options={ALL_PAYMENT_TYPES}
							value={filters.paymentTypes}
							getOptionLabel={(o) => PAYMENT_TYPE_LABELS[o]}
							onChange={(_, val) => handleChange('paymentTypes', val)}
							disableCloseOnSelect
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip {...getTagProps({ index })} key={option} label={PAYMENT_TYPE_LABELS[option]} size="small" />
								))
							}
							renderInput={(params) => <TextField {...params} label="Tipo de pago" size="small" />}
						/>
					</Grid>

					{/* Estado — multiselect */}
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							multiple
							options={ALL_STATUSES}
							value={filters.paymentStatuses}
							getOptionLabel={(o) => PROVIDER_PAYABLE_LINE_STATUS_LABEL[o]}
							onChange={(_, val) => handleChange('paymentStatuses', val)}
							disableCloseOnSelect
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										{...getTagProps({ index })}
										key={option}
										label={PROVIDER_PAYABLE_LINE_STATUS_LABEL[option]}
										size="small"
									/>
								))
							}
							renderInput={(params) => <TextField {...params} label="Estado" size="small" />}
						/>
					</Grid>

					{/* Fecha real de pago — desde/hasta */}
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Pago real desde"
							value={filters.dateFrom}
							onChange={(d) => handleChange('dateFrom', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Pago real hasta"
							value={filters.dateTo}
							onChange={(d) => handleChange('dateTo', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>

					{/* Fecha de pago programado (schedule) — desde/hasta */}
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Vto. programado desde"
							value={filters.scheduleDateFrom}
							onChange={(d) => handleChange('scheduleDateFrom', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Vto. programado hasta"
							value={filters.scheduleDateTo}
							onChange={(d) => handleChange('scheduleDateTo', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
				</Grid>
			</Paper>
		</LocalizationProvider>
	);
};
